/**
 * @file AddDomainDialog.tsx
 * @description «Добавить домен» магазину (phase19.28 этап А;
 * uiux-platform-shops §5, §7; PL11, OQ-149: www — переадресацией).
 *
 * Адрес принимается в любом виде (`https://Lavka.RU/` → `lavka.ru`): под
 * полем сразу видно, что сохранится, у IDN — Юникод и punycode
 * («магазин.рф (xn--80a…)»). Проверка — общая `normalizeHost` (как у
 * API), окончательно решает сервер (422 / 409 «уже привязан к магазину X»).
 * Галочка «www как переадресация» включена по умолчанию. Новый адрес —
 * переадресация на главный: главным его делают, когда он заработает (В11).
 */

import { hostToUnicode, normalizeHost, NORMALIZE_HOST_ERROR_MESSAGES } from "@chaiberi/shared";
import { useState } from "react";

import { platformShops, type ShopDomain } from "../../../lib/api-platform-shops";
import { usePlatformEdgeConfig } from "../../../hooks/platform/usePlatformEdgeConfig";
import { hostPreview } from "../../../lib/platform/shop-domain-status";
import { shopDomainError } from "../../../lib/platform/shop-errors";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useNotify } from "../../ui/NotifyProvider";
import { ShopTextField } from "./ShopTextField";

interface Props {
  shopId: string;
  /** Адрес из журнала неизвестных — подставляется в поле. */
  initialHost?: string;
  onClose: () => void;
  onAdded: (domain: ShopDomain) => void;
}

export function AddDomainDialog({ shopId, initialHost = "", onClose, onAdded }: Props) {
  const notify = useNotify();
  const [input, setInput] = useState(initialHost);
  const [note, setNote] = useState("");
  const [withWww, setWithWww] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { baseDomain } = usePlatformEdgeConfig();
  const parsed = input.trim() ? normalizeHost(input, { kind: "CUSTOM", platformBaseDomain: baseDomain }) : null;
  const host = parsed?.data ?? null;
  const offerWww = !!host && !host.startsWith("www.");
  const hint = host ? `Сохранится: ${hostPreview(host)}` : "Например, lavka.ru — можно вставить адрес из браузера";

  async function submit() {
    if (!parsed || parsed.error) {
      setError(parsed?.error ? NORMALIZE_HOST_ERROR_MESSAGES[parsed.error] : "Укажите адрес сайта.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const domain = await platformShops.addDomain(shopId, { host: parsed.data, ...(note.trim() ? { note: note.trim() } : {}) });
      if (offerWww && withWww) {
        // www — отдельный адрес-переадресация; его отказ не отменяет основной.
        await platformShops.addDomain(shopId, { host: `www.${parsed.data}` }).catch((err: unknown) => {
          notify.warning(`www.${parsed.data} не добавлен: ${shopDomainError(err)}`);
        });
      }
      notify.success(`Адрес ${hostPreview(parsed.data)} добавлен`);
      onAdded(domain);
    } catch (err) {
      setError(shopDomainError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent fullScreen className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Добавить домен</DialogTitle>
          <DialogDescription>
            Свой домен магазина. Он станет переадресацией на главный адрес; главным его можно сделать, когда он заработает.
          </DialogDescription>
        </DialogHeader>
        <form
          id="add-domain-form"
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <ShopTextField
            id="add-domain-host"
            label="Адрес"
            required
            value={input}
            onChange={(value) => {
              setInput(value);
              setError(null);
            }}
            error={error ?? undefined}
            hint={hint}
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            disabled={busy}
          />
          {offerWww ? (
            <label className="type-body flex min-h-12 cursor-pointer items-center gap-3">
              <Checkbox checked={withWww} onCheckedChange={(checked) => setWithWww(checked === true)} disabled={busy} />
              <span className="min-w-0 break-words">Добавить www.{hostToUnicode(host ?? "")} как переадресацию (рекомендуем)</span>
            </label>
          ) : null}
          <ShopTextField
            id="add-domain-note"
            label="Заметка"
            value={note}
            onChange={setNote}
            hint="Необязательно: чей домен, где регистратор"
            maxLength={200}
            disabled={busy}
          />
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button type="submit" form="add-domain-form" loading={busy}>
            Добавить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
